import React, { useState, useEffect, useRef, useCallback } from "react";
import { debounce } from "lodash";
import { Button, Dropdown, DropdownDivider, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faPlus } from "@fortawesome/free-solid-svg-icons";
import exportIcon from "../../assets/export-icon.svg";
import deactivateIcon from "../../assets/deactivate.svg";
import search from "../../assets/search.svg";
import { CSVLink } from "react-csv";
import { DownloadTableExcel } from "react-export-table-to-excel";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import orderRoute from "../../services/orderRoute";
import { useAppContext } from "../../AppContext";
import { use } from "react";
import { useNavigate } from "react-router-dom";
import DeleteProductModal from "../../components/Modal/DeleteProductModal";
import BulkUpdateModal from "../../components/Modal/BulkUpdateModal";
import SetCutOffTimeModal from "../../components/Modal/SetCutOffTimeModal";
import LocationsTable from "../../components/DeliveryLocations/LocationsTable";
import CutOffTimesTable from "../../components/DeliveryLocations/CutOffTimesTable";

const CustomToggle = React.forwardRef(({ children, onClick, className, variant, id, style }, ref) => (
  <Button
    ref={ref}
    variant={variant || "secondary"}
    id={id}
    style={style}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className={`${className} d-flex align-items-center gap-2`}
  >
    {children}
  </Button>
));

const CUTOFF_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

// Backend defaults every day to a 10:00 cut-off; seed the listing with that.
const buildDefaultCutOffTimes = () =>
  CUTOFF_DAYS.map((day) => ({ day, cutOffTime: "10:00", startDate: "", endDate: "" }));

const DeliveryLocations = () => {
  const { showToast, setShowDeleteModal } = useAppContext();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [displayLocations, setDisplayLocations] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedState, setSelectedState] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedDeliveryDays, setSelectedDeliveryDays] = useState([]);
  const {
    getDeliveryLocations,
    getDeliveryStates,
    getGlobalDeliveryLocations,
    getCutOffTimes,
  } = orderRoute();
  const [deliveryStates, setDeliveryStates] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 20,
    totalPages: 0,
    totalLocations: 0,
  });
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedLocationsId, setSelectedLocationsId] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showCutOffModal, setShowCutOffModal] = useState(false);
  const [activeTab, setActiveTab] = useState("Delivery Locations");
  const [cutOffTimes, setCutOffTimes] = useState(buildDefaultCutOffTimes());
  const [cutOffLoading, setCutOffLoading] = useState(false);
  const [bulkAction, setBulkAction] = useState("");
  const [selectedLgaNames, setSelectedLgaNames] = useState([]);
  const [allGlobalLgas, setAllGlobalLgas] = useState([]);

  const tableRef = useRef(null);
  const filterDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const [isStateFilterOpen, setIsStateFilterOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isDeliveryDaysOpen, setIsDeliveryDaysOpen] = useState(false);
  const stateFilterDropdownRef = useRef(null);
  const statusFilterDropdownRef = useRef(null);
  const deliveryDaysDropdownRef = useRef(null);
  const prevFiltersRef = useRef("");

  const toggleStateFilterDropdown = () => setIsStateFilterOpen((prev) => !prev);
  const toggleStatusFilterDropdown = () => setIsStatusFilterOpen((prev) => !prev);
  const toggleDeliveryDaysDropdown = () => setIsDeliveryDaysOpen((prev) => !prev);

  useEffect(() => {
    getDeliveryStates(showToast, setDeliveryStates);
    getGlobalDeliveryLocations(showToast, (allLgas) => {
      setAllGlobalLgas(allLgas);
    });
  }, []);

  const loadCutOffTimes = () => {
    getCutOffTimes(setCutOffLoading, showToast, setCutOffTimes);
  };

  useEffect(() => {
    if (activeTab === "Cut-off Times") {
      loadCutOffTimes();
    }
  }, [activeTab]);

  useEffect(() => {
    const currentFilters = JSON.stringify({
      searchValue,
      selectedState,
      selectedStatus,
      selectedDeliveryDays,
    });

    const debouncedFetch = debounce(() => {
      if (prevFiltersRef.current !== currentFilters && pagination.currentPage !== 1) {
        prevFiltersRef.current = currentFilters;
        handlePageChange(1);
        return;
      }

      prevFiltersRef.current = currentFilters;

      const body = {
        pageParam: pagination.currentPage,
        sizeParam: pagination.itemsPerPage || 20,
      };

      if (searchValue) body.search = searchValue;
      if (selectedState !== "All") body.state = selectedState;
      if (selectedDeliveryDays.length > 0)
        body.deliveryDays = selectedDeliveryDays.map((day) => day.toLowerCase());
      if (selectedStatus !== "All") body.status = selectedStatus.toLowerCase();

      getDeliveryLocations(
        body,
        setLoading,
        showToast,
        setLocations,
        setDisplayLocations,
        setPagination
      );
    }, 500);

    debouncedFetch();

    return () => {
      debouncedFetch.cancel();
    };
  }, [
    searchValue,
    selectedState,
    selectedStatus,
    selectedDeliveryDays,
    pagination.currentPage,
    pagination.itemsPerPage,
  ]);

  const handlePageChange = (page) => {
    setPagination((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const handleStateFilter = (state) => {
    setSelectedState(state);
    setIsStateFilterOpen(false);
  };

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    setIsStatusFilterOpen(false);
  };

  const handleDeliveryDayFilter = (day) => {
    setSelectedDeliveryDays((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      } else {
        return [...prev, day];
      }
    });
  };

  const clearDeliveryDays = () => {
    setSelectedDeliveryDays([]);
  };

  const toggleFilterDropdown = () => setIsFilterOpen(!isFilterOpen);
  const toggleExportDropdown = () => setIsExportOpen(!isExportOpen);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const handleToggleSelectAll = () => {
    const currentPageIds = displayLocations.map((loc) => loc.id || loc?._id);
    const allCurrentSelected =
      currentPageIds.length > 0 && currentPageIds.every((id) => selectedLocationsId.includes(id));

    if (allCurrentSelected) {
      setSelectedLocationsId((prev) => prev.filter((id) => !currentPageIds.includes(id)));
      setSelectedLgaNames((prev) => {
        const currentPageNames = displayLocations.map((loc) => loc.name);
        return prev.filter((name) => !currentPageNames.includes(name));
      });
    } else {
      const missingIds = currentPageIds.filter((id) => !selectedLocationsId.includes(id));
      const missingNames = displayLocations
        .filter((loc) => missingIds.includes(loc.id || loc?._id))
        .map((loc) => loc.name);

      setSelectedLocationsId((prev) => [...prev, ...missingIds]);
      setSelectedLgaNames((prev) => [...prev, ...missingNames]);
    }
  };

  const handleCheckboxChange = (locId, locName, isChecked) => {
    if (isChecked) {
      setSelectedLocationsId((prev) => [...prev, locId]);
      setSelectedLgaNames((prev) => [...prev, locName]);
    } else {
      setSelectedLocationsId((prev) => prev.filter((id) => id !== locId));
      setSelectedLgaNames((prev) => prev.filter((name) => name !== locName));
    }
  };

  const handleBulkUpdate = (action, isGeneral = false) => {
    setBulkAction(action);
    if (isGeneral) {
      if (allGlobalLgas.length > 0) {
        const ids = allGlobalLgas.map((loc) => loc.id || loc?._id);
        const names = allGlobalLgas.map((loc) => loc.name);
        setSelectedLocationsId(ids);
        setSelectedLgaNames(names);
        setShowBulkModal(true);
      } else {
        getGlobalDeliveryLocations(showToast, (allLgas) => {
          setAllGlobalLgas(allLgas);
          const ids = allLgas.map((loc) => loc.id || loc?._id);
          const names = allLgas.map((loc) => loc.name);
          setSelectedLocationsId(ids);
          setSelectedLgaNames(names);
          setShowBulkModal(true);
        });
      }
    } else {
      setShowBulkModal(true);
    }
  };

  const handlePDFExport = () => {
    const input = document.getElementById("delivery-table");
    html2canvas(input).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save("delivery-locations.pdf");
    });
  };

  const handleCSVExport = () => {
    const csvData = prepareCSVData();
    if (csvData) {
      return (
        <CSVLink
          data={csvData.data}
          headers={csvData.headers}
          filename="delivery-locations-list.csv"
          className="csv-link"
        >
          As CSV
        </CSVLink>
      );
    } else {
      console.error("CSV data is not available.");
    }
  };

  const prepareCSVData = () => {
    if (locations?.length === 0) {
      console.warn("No locations available for CSV export.");
      return null;
    }

    const headers = [
      { label: "Delivery Area", key: "area" },
      { label: "State", key: "state" },
      { label: "Delivery Fee", key: "fee" },
      { label: "Delivery Day(s)", key: "days" },
      { label: "Status", key: "status" },
    ];

    const data = locations?.map((loc) => ({
      area: loc?.name,
      state: loc?.state?.name,
      fee: loc?.deliveryFee,
      days: loc?.days || "-",
      status: loc?.status,
    }));

    return { headers, data };
  };

  return (
    <section className="list-page">
      <div
        className="d-flex justify-content-between align-items-center header-admin py-4 pe-5 mb-4 ps-4"
        fixed="top"
      >
        <h3 style={{ color: "#344054" }}> Delivery Locations</h3>
        <div className="d-flex gap-3">
          {activeTab === "Delivery Locations" ? (
            <>
              {!loading && (
                <Dropdown show={isExportOpen} ref={exportDropdownRef}>
                  <Button
                    variant="secondary"
                    id="dropdown-basic"
                    style={{ fontWeight: "500", color: "#333333" }}
                    onClick={toggleExportDropdown}
                    className="d-flex align-items-center gap-2 px-3 py-2"
                  >
                    <img src={exportIcon} alt="export" />
                    Export
                  </Button>
                  <Dropdown.Menu>
                    <Dropdown.Item>
                      <DownloadTableExcel
                        filename="delivery-locations"
                        sheet="locations"
                        currentTableRef={tableRef.current}
                      >
                        <button style={{ border: "none", backgroundColor: "transparent" }}>
                          As Excel
                        </button>
                      </DownloadTableExcel>
                    </Dropdown.Item>
                    <DropdownDivider />
                    <Dropdown.Item onClick={handlePDFExport}>As PDF</Dropdown.Item>
                    <DropdownDivider />
                    {handleCSVExport()}
                  </Dropdown.Menu>
                </Dropdown>
              )}
              <Dropdown>
                <Dropdown.Toggle
                  as={CustomToggle}
                  variant="secondary"
                  id="bulk-update-dropdown"
                  className="no-caret"
                >
                  Update Locations <FontAwesomeIcon icon={faChevronDown} />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleBulkUpdate("activate", true)}>
                    Activate
                  </Dropdown.Item>
                  <DropdownDivider />
                  <Dropdown.Item onClick={() => handleBulkUpdate("deactivate", true)}>
                    Deactivate
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              <Button
                variant="primary"
                className="d-flex align-items-center gap-2"
                onClick={() => navigate("/delivery-locations/add")}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add New Location
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              className="d-flex align-items-center gap-2 px-3 py-2"
              onClick={() => setShowCutOffModal(true)}
            >
              Update Cut-Off Time
            </Button>
          )}
        </div>
      </div>

      <div className="d-flex gap-5 mb-3 ps-4" style={{ borderBottom: "1px solid #EDEDED" }}>
        {["Delivery Locations", "Cut-off Times"].map((tab, index) => (
          <p
            key={index}
            className={`order-item pb-2 mb-0 me-4 ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
            style={{ cursor: "pointer", marginBottom: "5px" }}
          >
            {tab}
          </p>
        ))}
      </div>

      <div className="pe-5 ps-4 list-body">
        {activeTab === "Delivery Locations" ? (
          <>
        <div className="d-flex align-items-center justify-content-between my-3">
          <div className="d-flex align-items-center gap-3">
            {selectedLocationsId.length > 0 ? (
              <div className="d-flex align-items-center gap-3">
                <Form.Check
                  checked={
                    displayLocations.length > 0 &&
                    displayLocations.every((loc) =>
                      selectedLocationsId.includes(loc.id || loc?._id)
                    )
                  }
                  label={`${selectedLocationsId.length} selected`}
                  className="selected-check"
                  onClick={handleToggleSelectAll}
                />
                <Dropdown>
                  <Dropdown.Toggle
                    as={CustomToggle}
                    variant="secondary"
                    className="py-1 px-3 no-caret"
                    style={{ fontSize: "12px", fontWeight: "500" }}
                  >
                    Update Locations <FontAwesomeIcon icon={faChevronDown} />
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => handleBulkUpdate("activate")}>
                      Activate
                    </Dropdown.Item>
                    <DropdownDivider />
                    <Dropdown.Item onClick={() => handleBulkUpdate("deactivate")}>
                      Deactivate
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            ) : (
              <p style={{ color: "#1D2939", fontWeight: "500" }} className="my-auto">
                {pagination?.totalLocations || displayLocations.length}{" "}
                {(pagination?.totalLocations || displayLocations.length) > 1
                  ? "Locations"
                  : "Location"}
              </p>
            )}

            <div
              className="d-flex align-items-center position-relative search-bar py-1 px-1"
              style={{ border: "1px solid #D9D9D9", fontWeight: "500", width: "300px" }}
            >
              {!isFocused && searchValue === "" && (
                <img
                  src={search}
                  alt="search"
                  style={{ top: "24%", left: "1.5%" }}
                  className="position-absolute"
                />
              )}
              {searchValue !== "" && (
                <i
                  className="bi bi-x"
                  style={{
                    top: "5%",
                    right: "1.5%",
                    position: "absolute",
                    fontSize: "1.5rem",
                    color: "#98A2B3",
                    cursor: "pointer",
                  }}
                  onClick={() => setSearchValue("")}
                ></i>
              )}
              <Form.Control
                style={{ border: "none", backgroundColor: "transparent" }}
                placeholder="Search by delivery area"
                className="w-100"
                value={searchValue}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
          </div>

          <div className="d-flex gap-2">
            <Dropdown show={isStateFilterOpen} ref={stateFilterDropdownRef}>
              <Button
                variant="secondary"
                id="dropdown-basic"
                onClick={toggleStateFilterDropdown}
                className="d-flex align-items-center gap-2"
                style={{ fontWeight: "500", color: "#333333" }}
              >
                State
                <FontAwesomeIcon icon={faChevronDown} />
              </Button>

              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => handleStateFilter("All")}
                  className="d-flex justify-content-between align-items-center"
                >
                  All
                </Dropdown.Item>
                <DropdownDivider />
                {deliveryStates.map((state, index) => (
                  <Dropdown.Item key={index} onClick={() => handleStateFilter(state?.id)}>
                    {state?.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown show={isDeliveryDaysOpen} ref={deliveryDaysDropdownRef}>
              <Button
                variant="secondary"
                id="dropdown-basic"
                onClick={toggleDeliveryDaysDropdown}
                className="d-flex align-items-center gap-2"
                style={{ fontWeight: "500", color: "#333333" }}
              >
                Delivery Days
                <FontAwesomeIcon icon={faChevronDown} />
              </Button>

              <Dropdown.Menu>
                <div className="px-3 py-2" style={{ minWidth: "200px" }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted fw-semibold">Select days</small>
                    {selectedDeliveryDays.length > 0 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-primary text-decoration-none"
                        style={{ fontSize: "0.875rem" }}
                        onClick={clearDeliveryDays}
                      >
                        Clear all
                      </Button>
                    )}
                  </div>
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].map((day, index) => (
                    <Form.Check
                      key={index}
                      type="checkbox"
                      id={`day-${day}`}
                      label={day}
                      checked={selectedDeliveryDays.includes(day)}
                      onChange={() => handleDeliveryDayFilter(day)}
                      className="mb-2"
                    />
                  ))}
                </div>
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown show={isStatusFilterOpen} ref={statusFilterDropdownRef}>
              <Button
                variant="secondary"
                id="dropdown-basic"
                onClick={toggleStatusFilterDropdown}
                className="d-flex align-items-center gap-2"
                style={{ fontWeight: "500", color: "#333333" }}
              >
                Status
                <FontAwesomeIcon icon={faChevronDown} />
              </Button>

              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => handleStatusFilter("All")}
                  className="d-flex justify-content-between align-items-center"
                >
                  All
                </Dropdown.Item>
                <DropdownDivider />
                {["Active", "Inactive"].map((status, index) => (
                  <Dropdown.Item key={index} onClick={() => handleStatusFilter(status)}>
                    {status}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {loading ? (
          <div
            className="justify-content-center align-items-center text-center"
            style={{ padding: "100px" }}
          >
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : displayLocations?.length > 0 ? (
          <LocationsTable
            displayLocations={displayLocations}
            selectedLocationsId={selectedLocationsId}
            handleToggleSelectAll={handleToggleSelectAll}
            handleCheckboxChange={handleCheckboxChange}
            tableRef={tableRef}
            pagination={pagination}
            handlePageChange={handlePageChange}
            navigate={navigate}
            setCurrentLocation={setCurrentLocation}
            setShowDeleteModal={setShowDeleteModal}
          />
        ) : (
          <div className="fw-medium" style={{ padding: "50px 0" }}>
            <h4>There are no locations</h4>
          </div>
        )}
          </>
        ) : cutOffLoading ? (
          <div
            className="justify-content-center align-items-center text-center"
            style={{ padding: "100px" }}
          >
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <CutOffTimesTable cutOffTimes={cutOffTimes} />
        )}
      </div>
      <DeleteProductModal item={currentLocation} itemType="location" actionType="deactivate" />
      <BulkUpdateModal
        show={showBulkModal}
        onHide={() => setShowBulkModal(false)}
        action={bulkAction}
        selectedLgaIds={selectedLocationsId}
        selectedLgaNames={selectedLgaNames}
        onSuccess={() => {
          setShowBulkModal(false);
          setSelectedLocationsId([]);
          setSelectedLgaNames([]);
          window.location.reload();
        }}
      />
      <SetCutOffTimeModal
        show={showCutOffModal}
        onHide={() => setShowCutOffModal(false)}
        currentCutOffTimes={cutOffTimes}
        onSuccess={loadCutOffTimes}
      />
    </section>
  );
};

export default DeliveryLocations;